import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, ROLE_HOME } from "@/context/AuthContext";
import { CountryProvider } from "@/context/CountryContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Search from "@/pages/Search";
import ProductCompare from "@/pages/ProductCompare";
import Promotions from "@/pages/Promotions";
import Providers from "@/pages/Providers";
import ProviderDetail from "@/pages/ProviderDetail";
import Payment from "@/pages/Payment";
import Chat from "@/pages/Chat";
import ClientDashboard from "@/pages/ClientDashboard";
import MerchantDashboard from "@/pages/MerchantDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Restaurants from "@/pages/Restaurants";
import Hebergements from "@/pages/Hebergements";
import Transport from "@/pages/Transport";
import AgentDashboard from "@/pages/AgentDashboard";
import AgentCandidature from "@/pages/AgentCandidature";

function Shell({ children, hideChrome }) {
  return (
    <div className="min-h-screen flex flex-col">
      {!hideChrome && <Navbar />}
      <main className="flex-1">{children}</main>
      {!hideChrome && <Footer />}
    </div>
  );
}

function DashboardRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[user.role]} replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Shell><Landing /></Shell>} />
      <Route path="/login" element={<Shell><Login /></Shell>} />
      <Route path="/register" element={<Shell><Register /></Shell>} />
      <Route path="/recherche" element={<Shell><Search /></Shell>} />
      <Route path="/comparer" element={<Shell><ProductCompare /></Shell>} />
      <Route path="/promotions" element={<Shell><Promotions /></Shell>} />
      <Route path="/prestataires" element={<Shell><Providers /></Shell>} />
      <Route path="/prestataires/:id" element={<Shell><ProviderDetail /></Shell>} />
      <Route path="/restaurants" element={<Shell><Restaurants /></Shell>} />
      <Route path="/hebergements" element={<Shell><Hebergements /></Shell>} />
      <Route path="/transport" element={<Shell><Transport /></Shell>} />
      <Route path="/paiement/:txId" element={<Shell><ProtectedRoute><Payment /></ProtectedRoute></Shell>} />
      <Route path="/messages" element={<Shell><ProtectedRoute><Chat /></ProtectedRoute></Shell>} />
      <Route path="/dashboard" element={<DashboardRedirect />} />
      <Route path="/compte" element={<Shell><ProtectedRoute roles={["client"]}><ClientDashboard /></ProtectedRoute></Shell>} />
      <Route path="/commercant" element={<Shell><ProtectedRoute roles={["commercant"]}><MerchantDashboard /></ProtectedRoute></Shell>} />
      <Route path="/prestataire" element={<Shell><ProtectedRoute roles={["prestataire"]}><ProviderDashboard /></ProtectedRoute></Shell>} />
      <Route path="/admin" element={<Shell><ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute></Shell>} />
      <Route path="/agent" element={<Shell hideChrome={false}><ProtectedRoute roles={["agent","admin"]}><AgentDashboard /></ProtectedRoute></Shell>} />
      <Route path="/agent-candidature" element={<Shell><AgentCandidature /></Shell>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename="/lmp">
        <CountryProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </CountryProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
